import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { MemberNotificationPreferencesResponse } from "../../features/members/contracts/MemberNotificationPreferencesResponse";
import { UpdateMemberNotificationPreferencesRequest } from "../../features/portal/contracts/UpdateMemberNotificationPreferencesRequest";
import {
  fetchPortalNotificationPreferences,
  updatePortalNotificationPreferences,
} from "../../features/portal/slice-extensions/portal-slice-extension";

interface PreferenceSwitchProps {
  checked: boolean;
  disabled: boolean;
  label: string;
  hint: string;
  onChange: (next: boolean) => void;
}

const PreferenceSwitch = ({ checked, disabled, label, hint, onChange }: PreferenceSwitchProps) => (
  <label className="mt-3 flex cursor-pointer items-start gap-3">
    <input
      type="checkbox"
      className="mt-1"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span>
      <span className="block text-sm font-medium text-grey-20 dark:text-white">{label}</span>
      <span className="block text-sm text-grey-40 dark:text-grey-80">{hint}</span>
    </span>
  </label>
);

/**
 * The member's own control over the mail they can refuse: announcements, session
 * reminders and the email about an unread message. Other mail about their lessons and payments is not listed because it
 * cannot be switched off — saying so plainly is what makes switching off feel safe. They are always the
 * signed-in member's own, also while a guardian looks at a ward.
 */
interface PortalNotificationPreferencesCardProps {
  /** Shown while a ward is on screen: the settings are the signed-in member's own, so the card names them. */
  ownerName?: string;
}

const PortalNotificationPreferencesCard = ({ ownerName }: PortalNotificationPreferencesCardProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [preferences, setPreferences] = useState<MemberNotificationPreferencesResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    dispatch(fetchPortalNotificationPreferences())
      .unwrap()
      .then((loaded) => {
        if (active) setPreferences(loaded);
      })
      .catch(() => {
        if (active) setPreferences(null);
      });

    return () => {
      active = false;
    };
  }, [dispatch]);

  if (preferences === null) {
    return null;
  }

  const save = async (request: UpdateMemberNotificationPreferencesRequest) => {
    setBusy(true);
    setError(false);
    try {
      setPreferences(await dispatch(updatePortalNotificationPreferences(request)).unwrap());
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10">
      <h2 className="text-base font-semibold text-grey-20 dark:text-white">
        {t("portal.emailsTitle", "Email settings")}
      </h2>
      {ownerName && (
        <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">{t("portal.me.emailsFor", { name: ownerName })}</p>
      )}
      <PreferenceSwitch
        checked={preferences.announcementsEnabled}
        disabled={busy}
        label={t("portal.emailsAnnouncements", "Announcements and news")}
        hint={t(
          "portal.emailsAnnouncementsHint",
          "General messages from your instructor, like term dates or closures.",
        )}
        onChange={(next) => save({ announcementsEnabled: next })}
      />
      <PreferenceSwitch
        checked={preferences.sessionReminders}
        disabled={busy}
        label={t("portal.emailsReminders", "{{Session}} reminders")}
        hint={t(
          "portal.emailsRemindersHint",
          "An email before each booked {{session}}, with the free-cancellation deadline if there is one.",
        )}
        onChange={(next) => save({ sessionReminders: next })}
      />
      <PreferenceSwitch
        checked={preferences.messageNudges}
        disabled={busy}
        label={t("portal.emailsMessageNudges", "Email me when I have an unread message")}
        hint={t(
          "portal.emailsMessageNudgesHint",
          "One email when a message has waited ten minutes unread, at most one an hour per conversation.",
        )}
        onChange={(next) => save({ messageNudges: next })}
      />
      <p className="mt-3 text-sm text-grey-40 dark:text-grey-80">
        {t(
          "portal.emailsOperationalNote",
          "Booking confirmations, changes to your lessons and payment emails always arrive.",
        )}
      </p>
      {error && (
        <p className="field-error mt-2">
          {t("portal.emailsFailed", "Could not save your choice. Please try again.")}
        </p>
      )}
    </div>
  );
};

export default PortalNotificationPreferencesCard;
